import { useMessageStore } from "@/state/stores";
import { memo, useCallback } from "react";
import { useShallow } from "zustand/shallow";
import { MessageContent } from "./MessageContent";

interface MessageProps {
  messageId: string;
  projectId: string;
  isFinalMessage: boolean;
}

export const Message = memo(function Message({
  messageId,
  projectId,
  isFinalMessage,
}: MessageProps) {
  const message = useMessageStore(
    useShallow(({ messages }) => messages.find(({ id }) => id === messageId)),
  );

  if (!message) {
    return null;
  }

  // Check if user message likely has attachments (empty content but completed
  // status indicates attachments were sent)
  const hasUserAttachments =
    message.sender === "user" &&
    message.content === "" &&
    message.status === "completed";

  // For parts-enabled messages, check if we have any parts or if parts are still loading
  const hasPartsContent =
    message.usesParts && message.parts && message.parts.length > 0;

  const isWaitingForParts =
    message.usesParts &&
    (!message.parts || message.parts.length === 0) &&
    message.status === "processing";

  const hasSystemMessage = message.parts?.some(
    (part) => part.type === "tool-call" && part.toolName === "system",
  );

  const isCancelled = useMessageStore(
    useCallback(
      (state) =>
        message.status === "cancelled" ||
        (message.sender === "user" &&
          state.messages.some(
            (msg) =>
              msg.sender === "agent" &&
              msg.parentMessageId === message.id &&
              msg.status === "cancelled",
          )),
      [message.status, message.sender, message.id],
    ),
  );

  const isAgentThinking = useMessageStore((state) =>
    state.messages.some(
      (msg) =>
        msg.id === message.id &&
        msg.sender === "agent" &&
        (msg.status === "processing" || msg.status === "pending"),
    ),
  );

  const originalMessage = useMessageStore(
    useCallback(
      (state) =>
        message.parentMessageId
          ? state.messages.find((msg) => msg.id === message.parentMessageId)
              ?.content
          : "",
      [message.parentMessageId],
    ),
  );

  return (
    <MessageContent
      type="persisted"
      id={message.id}
      sender={message.sender}
      content={message.content}
      status={message.status}
      isCancelled={isCancelled}
      hasUserAttachments={hasUserAttachments}
      originalMessage={originalMessage}
      projectId={projectId}
      usesParts={message.usesParts}
      hasPartsContent={hasPartsContent}
      parts={message.parts}
      isWaitingForParts={isWaitingForParts}
      isAgentThinking={isAgentThinking}
      isFinalMessage={isFinalMessage}
      hasSystemMessage={hasSystemMessage}
    />
  );
});
